import { PageContainer } from '@/bizComponents/pageContainer';
import { useAppContext } from '@/hooks/useAppContext';
import { useCurrentRelationType } from '@/hooks/useCurrentRelationType';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import { Page404 } from '@/pages/404';
import { getInitialPropsFromServer } from '@/ssr/getInitialPropsFromServer';
import { toCommaNumber } from '@/utils/format/toCommaNumber';
import { isSmallScreen } from '@/utils/isSmallScreen';
import {
  BrandAnalytics,
  BrandPageData,
  createSemanticUrl,
  getBrandPageData,
  parseSemanticUrl,
} from '@zbanx-coding/components-shared';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet, useLocation } from 'umi';

export interface Props {
  show404page?: boolean;
  initBrandData?: BrandPageData;
}

export const BrandsPage: React.FC<Props> = ({ show404page, initBrandData }) => {
  const { t } = useTranslation();
  const { language } = useAppContext();
  const { pathname } = useLocation();
  const relationType = useCurrentRelationType();
  const windowWidth = useWindowWidth();
  const brandId =
    initBrandData?.info?.productBrand.id || parseSemanticUrl(pathname);

  if (show404page || !brandId) {
    return <Page404 />;
  }

  const getNumText = (num?: number) => {
    return num ? toCommaNumber(num) : t('many');
  };

  return (
    <PageContainer>
      <BrandAnalytics
        language={language}
        relationType={relationType}
        brandId={brandId}
        initBrandData={initBrandData}
        size={isSmallScreen(windowWidth) ? 'small' : 'default'}
        renderMeta={({ details }) => {
          return details ? (
            <Helmet>
              <title>
                {t('page.brands.seo.title', {
                  name: details.info?.productBrand.name
                    ? `${details.info.productBrand.name} `
                    : '',
                })}
              </title>
              <meta
                name="description"
                content={t('page.brands.seo.desc', {
                  name: details.info?.productBrand.name,
                  resourceNum: getNumText(details.statistics?.totalYoutuber),
                  countryNum: getNumText(details.statistics?.totalCountry),
                  videoNum: getNumText(details.statistics?.totalVideo),
                })}
              />
            </Helmet>
          ) : null;
        }}
      />
    </PageContainer>
  );
};

getInitialPropsFromServer<Props>(
  BrandsPage,
  async ({ pathname, httpClient }) => {
    const brandId = parseSemanticUrl(pathname);

    if (!brandId) {
      return { show404page: true };
    }

    const data = await getBrandPageData(brandId, httpClient).catch(
      () => undefined,
    );

    if (!data) {
      return { show404page: true };
    }

    const last = createSemanticUrl(data.info?.productBrand.name, brandId);

    if (pathname !== `/youtube/brands/${last}`) {
      return { show404page: true };
    }

    return { initBrandData: data };
  },
);

export default BrandsPage;
